import { gql } from 'graphql-request';

import type { Address, BillingPeriod } from '../types';

import { axiosQuery } from '../utils/axiosQuery';
import { isStrictNever } from '../utils/isStrictNever';
import { GatewayError, GatweayErrorCode } from '../utils/SupaError';

interface ApiCreateSubscriptionSuccess {
  __typename: 'ApiCreateSubscriptionSuccess';
  accountId: string;
  subscriptionId: string;
}

interface ApiPaymentGatewayFailedResponse {
  __typename: 'ApiPaymentGatewayFailedResponse';
  errorGroup: GatweayErrorCode;
  isRetryable: boolean;
  message: string;
}

interface CreateSubscriptionResponse {
  createMembershipSubscriptionV2: ApiCreateSubscriptionSuccess | ApiPaymentGatewayFailedResponse;
}

const CREATE_SUBSCRIPTION_MUTATION = gql`
  mutation CreateSubscription($membershipSubscriptionInput: ApiMembershipSubscriptionInput!) {
    createMembershipSubscriptionV2(membershipSubscriptionInput: $membershipSubscriptionInput) {
      ... on ApiCreateSubscriptionSuccess {
        __typename
        accountId
        subscriptionId
      }
      ... on ApiPaymentGatewayFailedResponse {
        __typename
        errorGroup
        isRetryable
        message
      }
    }
  }
`;

export const createSubscription = async (
  paymentMethodId: string,
  userAddressForm: {
    billingAddress: Address;
    shippingAddress: Address;
  },
  paymentGateway: string,
  billingPeriod: BillingPeriod,
  email: string,
) => {
  const { billingAddress, shippingAddress } = userAddressForm;

  const billToContact = {
    ...billingAddress,
    personalEmail: email,
  };
  const shipToContact = {
    ...shippingAddress,
    personalEmail: email,
  };

  const membershipSubscriptionInput = {
    billToContact,
    shipToContact,
    paymentMethodId,
    paymentGatewayName: paymentGateway,
    billingPeriodType: billingPeriod,
  };

  const { createMembershipSubscriptionV2 } = await axiosQuery<CreateSubscriptionResponse>(
    'CreateSubscription',
    CREATE_SUBSCRIPTION_MUTATION,
    {
      membershipSubscriptionInput,
    },
  );

  switch (createMembershipSubscriptionV2.__typename) {
    case 'ApiCreateSubscriptionSuccess': {
      return createMembershipSubscriptionV2;
    }
    case 'ApiPaymentGatewayFailedResponse': {
      throw new GatewayError(
        createMembershipSubscriptionV2.message,
        createMembershipSubscriptionV2.errorGroup,
        createMembershipSubscriptionV2.isRetryable,
      );
    }
    default: {
      isStrictNever(createMembershipSubscriptionV2);
    }
  }
};
